/**
 * Scenario descriptions shown via the "📖 Scenario Info" button in the game header.
 * Add one entry per scenario ID matching the JSON file's "id" field.
 */
export const SCENARIO_DESCRIPTIONS: Record<string, { title: string; text: string }> = {

  'pegasus-bridge': {
    title: 'Pegasus Bridge — June 6, 1944',
    text: `HISTORICAL BACKGROUND

The late hours of June 5, 1944... Led by Major John Howard, the men of Oxford and Buckinghamshire Light Infantry step aboard six Horsa gliders on a secret airfield in Dorset. Their mission: to launch the first airborne assault of D-Day and capture two bridges, one astride the Caen Canal, the other over the Orne river, in Normandy.

A few minutes past midnight, now June 6, 1944, the gliders clip through the treetops and land in a small patch of rough field, between a pond and the Caen Canal, only yards away from their objective! Major Howard and his troops pour out of the planes, achieving complete surprise and rushing the stunned German forces. Despite furious opposition from a heavily sandbagged machine gun nest at the end of now famous "Pegasus" bridge, they capture their objective within moments. To the east, the Orne bridge is secured equally rapidly, giving British troops the first victory of D-Day!

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Axis Player:
Take 2 Command cards (Germans were surprised).
Draw two cards after turn 1, and two more after turn 2. You will now have 4 Command cards in hand for the rest of the battle.

Allied Player — British: Major John Howard.
Take 6 Command cards.
You move first.

─────────────────────────────────────

CONDITIONS OF VICTORY

4 Medals.

Any Allied unit that captures a Bridge hex counts as one Victory Medal. Place an Objective Medal on each of the two bridges. As long as an Allied unit remains on the Bridge hex, it continues to count toward the Allied victory. If the unit moves off the bridge or is eliminated, it no longer counts.

Note: Although all British units involved in the raid on Pegasus Bridge were elite forces, for the sake of simplicity this introductory scenario uses regular infantry units instead.`,
  },

  'sainte-mere-eglise': {
    title: 'Sainte-Mère-Église — June 6, 1944',
    text: `HISTORICAL BACKGROUND

Establishment of a defensive base at Ste. Mère-Église was one of the key objectives of the US 82nd Airborne Division. In contrast with other regiments, the 505th Parachute Infantry, landing northwest of Ste. Mère-Église, had one of the most accurate drops. Rapidly regrouping and tipped by a French native to the presence of German troops in town, the paratroopers planned to surround Ste. Mère-Église and move in with knives, bayonets and grenades.  

In the meantime, to the north, Lt. Turner Turnbull deployed his force on high ground near Neuville-au-Plain, engaging and fighting the enemy to a draw. This bought some crucial time for the battalions around Ste. Mère-Église, giving them a chance to meet the German southern thrust and annihilate several enemy units. This led to an overestimation by German command of American strength in this sector, and as a result, the Germans withdrew.  

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Allied Player — US 82nd Airborne Division.
Deploy paratroopers via airdrop at the start of the game. Choose a drop zone centre and scatter your forces into the fight.
Take 5 Command cards.
You move first.

Axis Player — German Garrison.
Take 4 Command cards.

─────────────────────────────────────

SPECIAL RULES:

Before the Allied side takes its first turn, the Allied player has an additional airdrop.  
Select a hex to as target for the drop. 4 units will be airdropped on the board with some randomness.
If a figure falls off the board or lands in a hex with another friendly or enemy unit, this figure is out of action and placed back into the box. No Victory Medal is awarded to the Axis player on this occasion.  
If the figure is not out-of-action, place an additional 3 figures from the box into the Infantry figure's hex. The unit landed safely and is ready for action.

_____________________________________
CONDITIONS OF VICTORY

[4 Medals.]`,
  },

  'sword-beach': {
    title: 'Sword Beach — June 6, 1944',
    text: `HISTORICAL BACKGROUND

The 8th Brigade Group of the 3rd British Division, supported by Commandos of the 1st Special Service Brigade and the amphibious Sherman tanks of the 13th and 18th Hussars, formed the first wave of Allied forces to land on "Sword" beach, near the mouth of the Orne River, on the morning of June 6, 1944.  

The tanks were to land first and engage the enemy before the infantry arrived. The heavy seas slowed the approach and both tanks and infantry came ashore together. The bad weather, coupled with stiff German resistance, delayed the inland advance. While commandos – including the first French troops to set foot back on their homeland that day – captured the Casino at Riva Bella, the bulk of the Allied forces failed to push inland and capture Caen, their objective for the day. The consequences of this, the biggest set-back of Allied operations on D-Day, were to be felt well into July, and the city of Caen itself would pay a dear price for it.  

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Allied Player — 8th Brigade Group.
Land your forces from the sea. Commandos may move 2 hexes and still battle.
Take 5 Command cards.
You move first.

Axis Player — 716th Static Infantry Division.
Defend the beach fortifications.
Take 4 Command cards.

─────────────────────────────────────

SPECIAL RULES:

The Axis player is in control of the Bunkers and may claim them as a defensive position.  
The Allied Special Forces are Commando units. Place a British badge in the same hex as these units to distinguish them from other units. Commandos may move 1 or 2 hexes and still battle.

─────────────────────────────────────

CONDITIONS OF VICTORY

[5 Medals.]`,
  },

  'pointe-du-hoc': {
    title: 'Pointe du Hoc — June 6, 1944',
    text: `HISTORICAL BACKGROUND

Planners felt that the German batteries on Pointe-du-Hoc would be one of the most daunting threats to Allied forces landing on Omaha in the morning of D-Day. Set atop 100 foot cliffs west of the beach, the guns' range were such that they'd be able to score direct hits on any troops landing on the beach below. The 2nd Rangers, under the command of Lieutenant Colonel James E. Rudder, were tasked with silencing them.  

Following a difficult approach and despite intense firing from the German garrison, the first of about 200 Rangers leading the assault were on top of the cliffs within minutes. They were surprised to find that the guns had been withdrawn off the point three days before. Finding only dummies made from timber in the gun casemates, Ranger patrols infiltrated south looking for the missing guns.  

Meanwhile two significant concentrations of Germans remained on the point for much of the morning. The anti-aircraft position in the southwest bunker and the observation bunker at the tip of the point were the most dangerous and resisted repeated Ranger attacks.  

A Ranger patrol finally found the guns unguarded, but ready to fire, in an apple orchard inland. The patrol placed incendiary thermite grenades in the guns and accomplished their mission.  

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Allied Player — 2nd Ranger Battalion.
Rangers may move 2 hexes and still battle. They can scale cliffs (hill hexes from beach cost 2 movement).
Take 6 Command cards.
You move first.

Axis Player — 726th Infantry Regiment.
Defend the plateau fortifications.
Take 4 Command cards.

─────────────────────────────────────

SPECIAL RULES:

The hill that runs along the beach is a cliff. Moving up the cliff from the beach is a 2 hex move. From the inland side, treat it as a normal hill in battle and for movement.  
The Axis player is in control of the Bunkers and may claim them as a defensive position.  
All the Allied units are Special Forces Ranger units. Therefore, there is no need to place Ranger badges with the units.  

Rangers may move 1 or 2 hexes and still battle.
─────────────────────────────────────

CONDITIONS OF VICTORY

[4 Medals]`,
  },

  'omaha-beach': {
    title: 'Omaha Beach — June 6, 1944',
    text: `HISTORICAL BACKGROUND

"Never had there been a dawn like this." - Cornelius Ryan, The Longest Day

The US assault waves on Omaha Beach were led by the 116th Regimental Combat Team, destined for Dog sector on the left; and the 16th Regimental Combat Team hitting Easy and Fox sectors. The first wave also included two battalions of tanks from the 741st and 743rd. A persistent swell and strong undertow kept pushing the incoming troops toward the wrong sections of the beach. Heavily-fortified German positions delivered a withering roll of fire the moment any LCA crash-landed in. Exhausted and sea-sick survivors reaching the water's edge would find no protection, save for a 200-yard dash to the seawall. Many simply collapsed, or tried to find cover behind the numerous beach obstructions. Yet in the face of intense small arms and artillery fire, a handful of young soldiers rallied and began to act. The first infiltrations were made between the E-1 and E-3 exits. Advances against other strong points were also successful and while it was not apparent at the time, German defenses were starting to crumble.

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Allied Player — 1st Infantry Division / 2nd Rangers.
Land your forces from the sea. Armor must use the beach draws to advance.
Rangers may move 2 hexes and still battle.
Take 5 Command cards.


Axis Player — 352nd Infantry Division.
Defend from the bluffs. You hold bunkers at the draw exits.
Take 4 Command cards.
You move first

─────────────────────────────────────

SPECIAL RULES:

The hill that runs along the beach is a bluff. Moving up the bluff from the beach is a 2 hex move. Tanks may not move up the bluff from the beach. From the inland side, treat the bluff as a normal hill in battle and for movement.  

The Axis player is in control of the Bunkers and may claim them as a defensive position.  

The Allied Special Force is a Ranger unit. Place a Rangers badge in the same hex as this unit to distinguish it from the other units. Rangers may move 1 or 2 hexes and still battle. (Note: Image shows a badge icon labeled "Rangers")  

Treat the Sea Wall as a permanent improved position (sandbags) for the Allied player. Place sandbags in these two hexes. Units may move through the seawall as normal.

─────────────────────────────────────

CONDITIONS OF VICTORY

6 Medals


Allied objectives: Colleville-sur-Mer, Saint-Laurent-sur-Mer, Vierville-sur-Mer (2 hexes).
The Axis scores medals by eliminating Allied landing units.`,
  },

  'mont-mouchet': {
    title: 'Mont Mouchet — June 10, 1944',
    text: `HISTORICAL BACKGROUND

On June 10th, three tactical groups from the Wehrmacht, about 2,000 men with armored support, converged on Mont Mouchet. Simultaneously moving in from the west (Saint-Flour), north (Langeac and Pinols) and east (Le Puy-en-Velay and Saugues) they were determined to trap all French Resistance troops in the area. Violent fighting occurred during the entire day. The French companies made use of their knowledge of the wooded and hilly terrain to hold off the advances and finally forced the enemy to temporarily fall back to their starting positions.

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Allied Player — French Resistance.
Take 5 Command cards.
You move first.

Axis Player — Brigade Jesser.
Take 4 Command cards.

─────────────────────────────────────

SPECIAL RULES:

French Resistance units have only 3 figures. A Resistance unit that has moved 1 hex may always battle after entering terrain that would normally forbid a standard unit from attacking after moving (forests, towns, hedgerows, etc.) — but unlike elite infantry, they do not get to move 2 hexes and battle. When retreating, a Resistance unit may retreat 1, 2, or 3 hexes for each Flag rolled (e.g. 2 Flags = 2 to 6 hexes), subject to the normal retreat rules.

Axis Special Forces tank units have 4 figures. Eliminating an Axis Special Forces tank unit counts as two Victory Medals for the Allied player.

─────────────────────────────────────

CONDITIONS OF VICTORY

4 Medals.

Allied objective: capture and hold the Mont Mouchet hexes.
Both sides score medals by eliminating enemy units (Axis Special Forces tanks count double).`,
  },

  'vassieux-vercors': {
    title: 'Vassieux, Vercors — July 21, 1944',
    text: `HISTORICAL BACKGROUND

Hotbed of the French Resistance, the "Maquis" of Vercors became one of the largest of its kind in the early months of 1944, with countless young French recruits joining up. Resistance camps were established around many villages such as Vassieux, because of its geographical configuration and strategic importance.

By July 14, 1944, the Resistance had completed the airfield — code-named "Taille-Crayon" (Pencil Sharpener) — and just received a drop from US flying fortresses when German planes appeared and bombed the village.

On July 21 at 9:00 AM, French Resistance fighters working to repair the airfield were taken by surprise when 20 German planes and gliders came flying out of the morning mist. The gliders landed and German troops poured out, making for the houses in the village. The Resistance around Vassieux rushed to help their comrades and dislodge the Germans from the buildings, but all attacks through the day failed.

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Axis Player:
Take 4 Command cards.
You move first.

Allied Player:
Take 4 Command cards.

─────────────────────────────────────

CONDITIONS OF VICTORY

4 Medals.

When "Their Finest Hour" command card is played, do not reshuffle the command deck. Should the command card deck run out of cards and the Allied player has not won, the battle ends as an Axis victory.

─────────────────────────────────────

SPECIAL RULES

All Allied units are French Resistance infantry. These units only have 3 figures. A Resistance unit may move onto terrain and still battle. When retreating, the unit may move back 1, 2, or 3 hexes before it regroups.`,
  },

  'operation-cobra': {
    title: 'Operation Cobra — July 25, 1944',
    text: `HISTORICAL BACKGROUND

On July 25, 1944, under the code name "Operation Cobra," the US First Army launched a major offensive along a 7-kilometre front, west of Saint-Lô. A devastating aerial bombardment — though not without mishaps — tore a gaping hole in the German defenses and opened the way for a rapid advance.

The 30th Division moved north from Hébrecrevon, the 9th Division drove toward Marigny, and the 4th Division pushed ahead along the Périers–Saint-Lô road. The German Panzer Lehr had been almost completely destroyed by the bombardment, while the 13th Parachute Regiment at Hébrecrevon offered determined resistance. By day's end, the German front had begun to crumble. The exploitation phase would see General Patton's armored columns racing toward Avranches and beyond — breaking the stalemate in Normandy once and for all.

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Allied Player — US VII Corps.
Take 5 Command cards.
You move first.

Axis Player — Wehrmacht.
Take 4 Command cards.

─────────────────────────────────────

SPECIAL RULES

The Axis Special Force tank unit has 4 figures.

─────────────────────────────────────

CONDITIONS OF VICTORY

5 Medals.

An Allied unit that captures the town of St. Gilles or Marigny counts as one Victory Medal. As long as an Allied unit remains on the town hex, it continues to count toward the Allied victory. If the unit moves off the hex or is eliminated, it no longer counts.`,
  },

  'mont-limar': {
    title: 'Montélimar — August 24–25, 1944',
    text: `HISTORICAL BACKGROUND

The Montélimar battle square, bounded on three sides by rivers, covered ground that alternated between flat open farmland and rugged hills and woods. Route N-7, the main north-south artery, runs along the Rhone River and was vital to the German forces.

By August 22nd, Allied forces had moved quickly north from the Anvil/Dragoon landing beaches and now threatened supplies moving down N-7 and this important northern escape route. Skirmishes broke out in the area, as both sides tried to determine the strength of the other side.

On the 24th, the fog of war for the German forces dissipated when a detailed copy of the Allies' operational plans for August 25th fell into their hands. The German plan of attack on the 25th was ambitious, but with a coordinated effort, the plan would cut off American forces on Hill 300 and in the Condillac Pass. Groupe Thiem in the north took Grane, but failed to advance south. The attacks against Hill 430 never began and although the 326th Grenadiers did take Bonlieu, they were driven back by American Artillery. The American effort to cut the N-7 road failed when a strong counterattack forced them to retire back into the hills. The action at Montélimar this day ended in a stalemate.

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Axis Player:
Take 5 Command cards.
You move first.

Allied Player:
Take 5 Command cards.

─────────────────────────────────────

CONDITIONS OF VICTORY

6 Medals.

An Axis unit that captures the town of Bonlieu or Grane counts as one Victory Medal. Place an Objective Medal in each of these towns. As long as the Axis unit remains on the Town hex, it continues to count toward the Axis victory. If the unit moves off or is eliminated, it no longer counts.

An Allied unit that captures any hex adjacent to the Rhone counts as one Victory Medal. Only one objective medal is gained, even if more than one hex is captured. As long as the Allied unit remains in an adjacent hex, it continues to count toward the Allied victory. If the unit moves off or is eliminated, it no longer counts.

─────────────────────────────────────

SPECIAL RULES

The Hill hexes north of the Condillac Pass (Allies' right flank) are impassable.

The Axis Special Force infantry units are elite Panzer Grenadier units. Place an Axis badge in the same hex as these units to distinguish them from the other units. The Panzer Grenadier units may move 1 or 2 hexes and still battle. The other German infantry units only have 3 figures.`,
  },

  'liberation-of-paris': {
    title: 'Liberation of Paris — August 24–25, 1944',
    text: `HISTORICAL BACKGROUND

Conscious of the highly symbolic and political importance of Paris, Allied forces initially planned to surround the city and wait for its capitulation rather than risk taking it, with the inherent costs of street-by-street fighting and obvious risks to the population.

Legend has it that Paris' garrison commander, General von Choltitz, was so moved by the city's beauty, as seen from his hotel room on the Faubourg Saint-Honoré, that he chose not to carry out Hitler's order to destroy the City. He also arranged a citywide truce with the Resistance.

It was perhaps this last action which set the Allied commanders thinking that they could take the city instead of bypassing it. Also, Generals Leclerc and de Gaulle insisted the city be liberated to restore French national pride. The mission was, therefore, assigned to the V Corps with Leclerc to lead the attack. But the approach into Paris was by no means as easy as expected. Choltitz had used the city truce to prepare the defenses outside the city. He was far from being ready to hand over the capital to the Allies without a fight.

Leclerc's Frenchmen attacked towards Paris at dawn on the 24th of August, in two columns. The left column, under Colonel de Langlade, immediately ran into German defenses, and was held up for hours in a firefight that saw the destruction of a number of German tanks. The right hand column, under Colonel Billotte, faced even more serious opposition and was quickly bogged down outside the capital by a series of strong points in several small villages.

That same night, defenses quickly evaporated when Choltitz ordered his troops to withdraw behind the Seine. The next day, August 25th, Choltitz surrendered to Leclerc at the Gare Montparnasse.

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Axis Player:
Take 4 Command cards.

Allied Player:
Take 5 Command cards.
You move first.

─────────────────────────────────────

CONDITIONS OF VICTORY

5 Medals.

A French unit that captures a Paris Outskirts town hex counts as one Victory Medal. Place an objective medal on each of these three town hexes. The medal, once gained, continues to count toward the Allied victory, even if the unit moves off the hex or is eliminated.`,
  },

  'toulon-campaign-for-southern-france': {
    title: 'Toulon — Campaign for Southern France, August 1944',
    text: `HISTORICAL BACKGROUND

Plans for the landings and campaign in southern France called for the French II Corps to take the port cities of Toulon and Marseille. Seeking to exploit German weakness in the area, General de Lattre de Tassigny pushed up the scheduled landings of the French units. Once ashore, he divided his forces into two commands: one under Lt. Gen. Edgar de Larminat consisting of two infantry divisions, some tanks and the African Commando Group; the other, under Maj. Gen. Aimé de Goislard de Monsabert, consisting of an infantry division, some tanks and a ranger unit.

Had the Germans had more time and material, they might have turned Toulon into a formidable fortress. But their existing defenses were not very strong, particularly on the landward approaches to the city.

The French attack was launched the morning of August 20th. Larminat's force clawed their way along the coastal road reducing German strong points one by one, but the drive from the northeast was stopped dead. De Monsabert's units on the other hand were more successful, swinging across rough terrain to outflank the city from the west and cutting the Toulon-Marseille highway.

During the next few days, the Germans were forced back into the city. As fighting continued, the German defense lost cohesion and on August 26th the isolated German command surrendered.

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Axis Player:
Take 4 Command cards.

Allied Player:
Take 6 Command cards.
You move first.

─────────────────────────────────────

CONDITIONS OF VICTORY

6 Medals.

If Allied units occupy 3 Town hexes in Toulon at the end of their turn, they win immediately.

─────────────────────────────────────

SPECIAL RULES

The Allied Special Forces are a Commando unit and a Ranger unit. Place the corresponding Special Forces badges in the same hex as these units to distinguish them from the other units. These units may move 1 or 2 hexes and still battle.`,
  },

  'operation-luttich': {
    title: 'Operation Lüttich — August 7, 1944',
    text: `HISTORICAL BACKGROUND

St. Barthélémy

"We must strike like lightning!" — Adolf Hitler to Field Marshall Von Kluge

Operation Lüttich began shortly before midnight on the evening of August 3rd. Under direct orders from Hitler, four armored divisions launched a counterattack westward across the Cotentin Peninsula toward Avranches. Their objective: to cut Patton's forces in half, thereby bottling up the Allied breakout.

The 2nd SS Panzer Division pushed into Mortain before dawn and sent a column toward St. Hilaire. But the value of holding Mortain was severely undermined by the 120th Infantry entrenched on Hill 317. The 2nd Panzer Division drove west along the River See until it was halted near Le Mesnil-Adelee. In the center, 1st SS Panzer and Panzer Lehr Division were stopped after taking St. Barthélémy. The northernmost element of the push, the 116th Panzer Division, failed to launch its attack.

The US commander, "Lightning Joe" Collins, countered and ordered armored and additional infantry forward from the reserve. By late morning, Allied aircraft swarmed the area; with a clear view of the German advance from Hill 317, the 120th was able to call in accurate air and artillery fire all day.

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Axis Player — Wehrmacht Panzer Corps.
Take 5 Command cards.
You move first.

Allied Player — US 30th Infantry Division.
Take 6 Command cards.

─────────────────────────────────────

CONDITIONS OF VICTORY

4 Medals.

An Axis unit that captures St. Hilaire counts as one Victory Medal. Place an objective medal in this town. As long as the Axis unit remains on the town hex, it continues to count toward the Axis victory. If the unit moves off or is eliminated, it no longer counts.

─────────────────────────────────────

SPECIAL RULES

Movement up onto a hill hex is a 2 hex move. Moving down off a hill or moving from a hill to an adjacent hill hex is a 1 hex move.

The Axis Special Force infantry is an elite Panzer Grenadier unit, and the Special Forces tank units have 4 figures. Place an Axis badge in the same hex as these units to distinguish them from the other units. The Panzer Grenadier unit may move 1 or 2 hexes and still battle.

As long as either hex of Hill 317 has an Allied unit on it, the Allied player may play his Recon cards as an Air Power strike for that section. Target a group of 4 or fewer adjacent hexes with enemy units on them. Air attack with 2 battle dice per hex (see Air Power card for more details).`,
  },

  'suomussalmi': {
    title: 'Suomussalmi — December 1939',
    text: `HISTORICAL BACKGROUND

In the opening weeks of the Winter War, the Soviet 163rd Division pushed into Finland along the Raate road, seizing the village of Suomussalmi. Stalin's planners expected a swift advance to the Gulf of Bothnia, splitting Finland in two.

Instead, Colonel Hjalmar Siilasvuo's Finnish 9th Division — outnumbered, but moving on skis through terrain the Red Army could not, and fighting in temperatures that fell to -40°C — encircled the road-bound Soviet columns in a series of isolated pockets, or "mottis." Cut off from resupply and unable to leave the roads, the 163rd Division was destroyed piecemeal.

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Allied Player — Soviet 163rd Division.
Hold the village and the road east. Rigid command structure: see "Commissar" special rule below.
Take 4 Command cards.

Axis Player — Finnish 9th Division.
Ski troops may move 3 hexes and still battle, at a shorter battle range, and may attack after entering terrain that would normally forbid it. They also retreat 1–3 hexes per Flag.
Take 6 Command cards.
You move first.

─────────────────────────────────────

SPECIAL RULES:

Frozen lakes may be crossed freely by any unit, including moving straight across in a single move — the ice does not stop movement. There is no battle or defense penalty for units on a frozen lake, but each hex of ice entered is a gamble: roll 2 dice, and lose 1 figure for every Star rolled. A unit that loses all its figures this way is destroyed.

Finnish Ski Troops move up to 3 hexes and may still battle, but their battle range is limited to 2 hexes. They may battle after entering forest, town, or hedgerow hexes regardless of how far they moved. When retreating, a Ski Troops unit may retreat 1, 2, or 3 hexes for each Flag rolled.

Commissar (Soviet player only): before Turn 1, place one Command card face-down under the Commissar token — this is committed to next turn. Each Soviet turn: place a new card under the token (committing to the turn after this one), then reveal and play whatever card was placed there last turn, then draw a replacement card as normal. Exception: a Recon card (any section) or Counter-Attack may be played directly from hand instead, leaving the token untouched for a future turn.

─────────────────────────────────────

CONDITIONS OF VICTORY

6 Medals.

Axis objective: capture and hold Suomussalmi village and Mäkinen's Roadblock on the eastern road.

Finnish special victory: if Finnish forces occupy 3 of the 4 town hexes (the three Suomussalmi hexes and Hulkoniemi) with live units at the end of any Finnish turn, Finland wins immediately, regardless of medal count.`,
  },

  'arnheim-bridge': {
    title: 'Arnhem Bridge — September 17–21, 1944',
    text: `HISTORICAL BACKGROUND

The largest Airborne drop ever designed, Operation Market Garden was Field Marshal Bernard Montgomery's plan to end the war on the Western Front early. If successful, it would threaten the flanks of German defenses along the Siegfried line and behind the Rhine River, helping an Allied crossing and precipitating the fall of Berlin. The operation famously ended up being "a bridge too far", handing Montgomery his only high-profile defeat.

At the Bridge, 2nd Battalion Lieutenant Colonel Frost commanded a force of about 500 men that took up a strong position at the northern end of the bridge. The Germans soon discovered that the airborne troops were a formidable enemy, and more than infantry assaults were needed to blast Frost's men out of their houses. A charge by the 9th SS Reconnaissance Battalion across the bridge was destroyed; likewise were attacks from the north. Despite being short of ammunition and with no food or water, the airborne force held the northern part of the bridge, denying the Germans a crossing place, until the night of 20/21 September. The last British resistance near the bridge ceased on September 23rd.

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Axis Player:
Take 6 Command cards.
You move first.

Allied Player:
Take 6 Command cards.

─────────────────────────────────────

CONDITIONS OF VICTORY

5 Medals.

─────────────────────────────────────

SPECIAL RULES

All Allied units are Special Forces Elite Airborne units. There is no need to place badges with the units. These units may move 1 or 2 hexes and still battle.

The Axis Special Forces tank units have 4 figures.

The Axis Special Forces infantry are Elite Grenadier units. Place an Axis special force token in the same hex with these units to distinguish them from the other units. The Elite Grenadier units may move 1 or 2 hexes and still battle.

Units may only enter or exit Arnheim Bridge from or onto the hexes as noted at the ends of the bridge. Movement along the span of the bridge is normal.

Infantry units may not target units on the opposite side of the Rhine River, which is too wide in this section.`,
  },

  'arracourt': {
    title: 'Arracourt — September 19, 1944',
    text: `HISTORICAL BACKGROUND

Part of the problem for the German forces involved in the Lorraine counter-offensive was the "Eastern" outlook of many of the units. They were unfamiliar with the US Army and its very different tactics. On the Eastern front, tank formations were used as a shock force to punch through infantry, because the Red Army had no anti-tank weapons and limited artillery support. This was not the case with the US Army, as would become painfully obvious in the following weeks.

The early morning attack of September 19th was planned as a two brigade assault with the 113th Panzer Brigade attacking Lezey, while the 111th Panzer Brigade drove toward Arracourt. But the 111th became lost during the night, after receiving bad instructions from a French farmer. The German column near Lezey emerged from the fog and was quickly engaged. Poor scouting of the area, prior to giving battle, put the German forces at a disadvantage, as the US tankers used the ridges in the area to conceal their movements. The tank battle intensified near Rechicourt with the US forces gaining the upper hand. Late in the day, Patton visited Arracourt. Believing German strength in the area had been spent, he ordered General Wood to continue his advance.

The German opportunity to hit the 4th US Armor Division with a concentrated blow was foiled by poor map reading and a French farmer.

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Axis Player:
Take 4 Command cards.
You move first.

Allied Player:
Take 6 Command cards.

─────────────────────────────────────

CONDITIONS OF VICTORY

6 Medals.

─────────────────────────────────────

SPECIAL RULES

None.`,
  },

  'st-vith-ardennes': {
    title: 'St-Vith, Ardennes — December 1944',
    text: `HISTORICAL BACKGROUND

As early as August 1944, Adolf Hitler began formulating plans for what would become the Ardennes offensive. While too ambitious for the available resources and terrain, the plan was deemed by the German High Command as having more chances of impacting the situation than throwing the same ad-hoc divisions into the increasingly desperate Eastern Front.

In the Ardennes, there were only a few cross-country roads, so forces would tend to pile up at the crossroads. St. Vith was one such town. The German tide rushed past to the north and south of St. Vith during the first days of the offensive, leaving the town to two Volksgrenadier divisions, supported by artillery and a few Tigers. In the defense, a hodge-podge force, including infantry and armor, dug in on a rugged ridge known as the Prumerberg, just east of St. Vith.

A heavy artillery barrage began the German attack. The infantry followed, advancing through gaps in the American line. The push was joined by Tiger tanks rolling up the front slopes of the Prumerberg. The American armor lay in wait on the ridge, but the Tigers, using an Eastern Front tactic of firing flares as they reached the crest, blinded the American tank crews and silhouetted the Sherman tanks. The American line broke under heavy pressure and fell back before additional forces could be deployed from the town.

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Axis Player:
Take 5 Command cards.
You move first.

Allied Player:
Take 4 Command cards.

─────────────────────────────────────

CONDITIONS OF VICTORY

6 Medals.

─────────────────────────────────────

SPECIAL RULES

The Axis Special Forces tank units have 4 figures.

The hill slope toward St. Vith is impassable.`,
  },

  'saverne-gap-vosges': {
    title: 'Saverne Gap, Vosges — November 1944',
    text: `HISTORICAL BACKGROUND

The Saverne Gap, cutting through the Vosges mountains, was the key to Strasbourg, capital city of Alsace. On November 21st, the US Seventh Army XV Corps, under the command of General Wade Hampton Haislip, arrived to the front lines at Phalsbourg.

With the infantry progressing forward into the Saverne Gap, General Leclerc divided his 2nd French Armor division into two task forces. The first would move well north of the Gap by La Petite-Pierre, the other on secondary roads in the south through heavily forested mountains by Dabo. If the plan worked, they would take Saverne simultaneously from both the north and south, avoiding the strong defenses expected in the Gap itself.

The plan worked to perfection. One of the south French armor group was even able to rush through Saverne's western end and climb to the Gap, taking the German defenses from behind. German forces, few in numbers, fought valiantly; but, without support or reserves, they were unable to stop the three-pronged Allied attack; they crumbled, leaving the door to Strasbourg wide open.

The stage is set, the battle lines are drawn, and you are in command. The rest is history.

─────────────────────────────────────

BRIEFING

Axis Player:
Take 4 Command cards.

Allied Player:
Take 6 Command cards.
You move first.

─────────────────────────────────────

CONDITIONS OF VICTORY

5 Medals.

If Allied units occupy 2 Town hexes in Saverne at the end of their turn, they win immediately.

─────────────────────────────────────

SPECIAL RULES

The hills are impassable.

Artillery may not fire over hills.`,
  },

};

